import type { SavedItem, SavedItemType, SaveItem, SaveItemFilterOptions, SaveItemGroup, SaveItemSortDirection, SaveItemSortOptions } from '@/types';

/** Display name for each item type; new items are also tagged with it. */
export const SHAPE_LABELS: Record<SavedItemType, string> = {
    point: 'Point',
    line: 'Line',
    polygon: 'Area',
    parcel: 'Parcel',
    none: 'None',
};

export const createSaveItem = (fields: Pick<SaveItem, 'itemType' | 'shape' | 'color' | 'size' | 'itemDetails'>): SaveItem => ({
    id: null,
    name: '',
    notes: '',
    tags: fields.itemType === 'none' ? [] : [SHAPE_LABELS[fields.itemType]],
    visible: true,
    createdAt: Date.now(),
    ...fields,
});

const NO_TAGS = 'No Tags';

/** Groups items by tag (an item appears under each of its tags), then sorts and filters. */
export function groupSavedItems(
    items: readonly SavedItem[],
    sortBy: SaveItemSortOptions,
    direction: SaveItemSortDirection,
    filter: SaveItemFilterOptions,
): SaveItemGroup[] {
    const sign = direction === 'asc' ? 1 : -1;
    const compareItems = (a: SavedItem, b: SavedItem) => sign * (sortBy === 'alpha' ? a.name.localeCompare(b.name) : a.createdAt - b.createdAt);

    const byTag = new Map<string, SavedItem[]>();
    for (const item of items) {
        for (const tag of item.tags.length > 0 ? item.tags : [NO_TAGS]) {
            byTag.set(tag, [...(byTag.get(tag) ?? []), item]);
        }
    }
    let groups = [...byTag].map(([tag, tagItems]) => ({
        tag,
        items: tagItems.filter((item) => filter !== 'Images' || item.hasImages).sort(compareItems),
    }));
    if (filter !== 'None' && filter !== 'Images') groups = groups.filter((group) => group.tag === filter);
    return groups
        .filter((group) => group.items.length > 0)
        .sort((a, b) => sign * (sortBy === 'alpha' ? a.tag.localeCompare(b.tag) : a.items[0].createdAt - b.items[0].createdAt));
}
